import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";
import {EquityPositionRecord} from "../types/EquityPositionRecord";
import {SecurityInformationRecord} from "../../../security/info/types/SecurityInformationRecord";
import {TechnicalIndicatorsRecord} from "../../../technical-indicators/types/TechnicalIndicatorsRecord";
import {Dividend} from "../../../dividends/types/DividendsRecord";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {IndustryGroup} from "../../../instruments/industry-group/types/IndustryGroup";
import {DividendsModel} from "../../../dividends/models/DividendsModel";
import {Optional} from "../../../js/util/Optional";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {CodeAndExchange} from "../../../instruments/types/CodeAndExchange";
import {Exchange} from "../../../instruments/exchange/Exchange";
import {EquityAccountId} from "../types/EquityAccountRecord";
import {PendingAveragePriceUpdateRecord} from "../../types/PendingAveragePriceUpdateRecord";
import {PortfolioType} from "../../../portfolio/models/Portfolio";

export type EquityPositionViewModels = ReadonlyArray<EquityPositionViewModel>;

export class EquityPositionViewModel implements CodeAndExchange {

    private constructor(public readonly accountId: EquityAccountId,
                        private readonly asAt: IsoDate,
                        private readonly equityPosition: EquityPositionRecord,
                        public readonly quote: QuoteViewModel | null,
                        public readonly priceStrategy: PriceStrategy,
                        private readonly securityInfo: SecurityInformationRecord | null,
                        private readonly currentDividend: Dividend | null,
                        private readonly indicators: TechnicalIndicatorsRecord | null,
                        public readonly portfolioType: PortfolioType,
                        public readonly pendingAveragePriceUpdate: PendingAveragePriceUpdateRecord | null) {
    }

    public get code(): string {
        return this.equityPosition.code;
    }

    public get exchange(): Exchange {
        return this.equityPosition.exchange;
    }

    public get quantity(): number {
        return this.equityPosition.quantity;
    }

    public get averagePrice(): number {
        return this.equityPosition.averagePrice;
    }

    public get previousClose(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.prevClose;
        }
    }

    public get open(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.open;
        }
    }

    public get last(): number | null {
        if (this.quote == null) {
            return null;
        } else {
            return this.quote.last;
        }
    }

    public get eodValue(): number | null {
        const previousClose = this.previousClose;
        if (previousClose == null) {
            return null;
        } else {
            return previousClose * this.quantity;
        }
    }

    public get todayPnL(): number {
        const previousClose = this.previousClose;
        const last = this.last;
        if ((previousClose == null) || (last == null)) {
            return 0;
        } else {
            const change = last - previousClose;
            return this.quantity * change;
        }
    }

    public get change(): number | null {
        const previousClose = this.previousClose;
        const last = this.last;
        if ((previousClose == null) || (last == null)) {
            return null;
        } else {
            return last - previousClose;
        }
    }

    public get changePercent(): number | null {
        const previousClose = this.previousClose;
        const last = this.last;
        if ((previousClose == null) || (last == null) || (last === 0) || (previousClose === 0)) {
            return null;
        } else {
            const change = last - previousClose;
            return change / previousClose;
        }
    }

    public get pnl(): number | null {
        const last = this.last;
        if (last == null) {
            return null;
        } else if ((this.equityPosition.averagePrice == null) || (this.equityPosition.averagePrice === 0)) {
            return null;
        } else {
            const currentValue = this.equityPosition.quantity * last;
            const openingValue = this.equityPosition.quantity * this.equityPosition.averagePrice;
            return currentValue - openingValue;
        }
    }

    public get pnlPercent(): number | null {
        const last = this.last;
        if (last == null) {
            return null;
        } else if ((this.equityPosition.averagePrice == null) || (this.equityPosition.averagePrice === 0)) {
            return null;
        } else {
            const currentValue = this.equityPosition.quantity * last;
            const openingValue = this.equityPosition.quantity * this.equityPosition.averagePrice;
            const pnl = currentValue - openingValue;

            if (openingValue === 0) {
                return null;
            }

            return pnl / openingValue;
        }
    }

    public get cost(): number | null {
        const averagePrice = this.equityPosition.averagePrice;
        if (averagePrice == null) {
            return null;
        } else {
            return this.equityPosition.quantity * averagePrice;
        }
    }

    public get totalValue(): number | null {
        const last = this.last;
        if (last == null) {
            return null;
        } else {
            return this.equityPosition.quantity * last;
        }
    }

    public get currentDividendExDate(): IsoDate | null {
        if (this.currentDividend == null) {
            return null;
        } else {
            return this.currentDividend.exDate;
        }
    }

    public get currentDividendPayableDate(): IsoDate | null {
        if (this.currentDividend == null) {
            return null;
        } else {
            return this.currentDividend.payableDate;
        }
    }

    public get currentDividendNotPaid(): boolean {
        if (this.currentDividend == null) {
            return false;
        } else {
            return this.currentDividend.payableDate >= this.asAt;
        }
    }

    public get currentDividendExPeriod(): boolean {
        if (this.currentDividend == null) {
            return false;
        } else {
            return (this.asAt >= this.currentDividend.exDate) && (this.asAt <= this.currentDividend.payableDate);
        }
    }

    public get dividendForecast(): number | null {
        if (this.currentDividend == null) {
            return null;
        } else {
            return this.currentDividend.value;
        }
    }

    // TODO Combine with quote
    public get yearPriceHigh(): number | null {
        if (this.indicators == null) {
            return null;
        } else {
            return this.indicators.yearPriceHigh;
        }
    }

    // TODO Combine with quote
    public get yearPriceLow(): number | null {
        if (this.indicators == null) {
            return null;
        } else {
            return this.indicators.yearPriceLow;
        }
    }

    public get industryGroup(): IndustryGroup | null {
        if (this.securityInfo == null) {
            return null;
        } else {
            return this.securityInfo.industryGroup;
        }
    }

    public get marketCap(): number | null {
        if (this.securityInfo == null) {
            return null;
        } else {
            return this.securityInfo.marketCap;
        }
    }

    public get peRatio(): number | null {
        const last = this.last;
        if ((this.securityInfo == null) || (last == null)) {
            return null;
        } else {
            const eps = this.securityInfo.earningsPerShare / 100; // Normalised to dollars
            if (eps === 0) {
                return null;
            }
            return last / eps;
        }
    }

    public get securityDescription(): string | null {
        if (this.securityInfo == null) {
            return null;
        } else {
            return this.securityInfo.companyName;
        }
    }

    public static create(accountId: EquityAccountId,
                         asAt: IsoDate,
                         equityPosition: EquityPositionRecord,
                         quote: QuoteViewModel | null,
                         securityInfo: SecurityInformationRecord | null,
                         dividends: DividendsModel | null,
                         techIndicators: TechnicalIndicatorsRecord | null,
                         pendingPriceUpdate: PendingAveragePriceUpdateRecord | null,
                         strategy: PriceStrategy,
                         portfolioType: PortfolioType): EquityPositionViewModel {
        const lastDividend = Optional.of(dividends).map((d) => d.last()).orNull();
        return new EquityPositionViewModel(accountId,
                                           asAt,
                                           equityPosition,
                                           quote,
                                           strategy,
                                           securityInfo,
                                           lastDividend,
                                           techIndicators,
                                           portfolioType,
                                           pendingPriceUpdate);
    }
}
